import { Form } from 'antd';
import { MasterSubCategory } from 'domain/entities/MasterEntity';
import { PurchaseDefaultEntity } from 'domain/entities/PurchaseEntity';
import { AppProps } from 'presentation/App';
import { AlertModalProps } from 'presentation/components/Modal/Modal.type';
import { openErrorNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import {
    completeFetching,
    showAlert,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import { commonHelpers } from 'utilities/helpers';
import { ButtonList } from './components/ButtonList';
import { StepViewOne } from './components/StepViewOne';
import { StepViewThree } from './components/StepViewThree';
import { StepViewTwo } from './components/StepViewTwo';

const MASTER_GROUP_CODES = [
    MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION,
    MASTER_CATEGORY_LIST.HOME_BUILDING_TYPE_CLASSIFICATION,
    MASTER_CATEGORY_LIST.HOME_POSSESSION_TYPE_CLASSIFICATION,
    MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_CLASSIFICATION,
];

const CU01502Page = (props: AppProps) => {
    const [searchParams] = useSearchParams();
    const purchaseOfferId = searchParams.get('purchaseOfferId') || '';
    const listedPropertyId = searchParams.get('listedPropertyId') || '';
    const procedureId = searchParams.get('procedureId') || '';
    const purchasePrice = searchParams.get('purchasePrice') || '';
    const loanUseStatus = searchParams.get('loanUseStatus') || '';
    const simId = searchParams.get('simId') || '';
    const navigate = useNavigate();

    const [form] = Form.useForm();

    const dispatch = useAppDispatch();

    const [masterData, setMasterData] = React.useState<any>({});
    const [data, setData] = React.useState<PurchaseDefaultEntity>({});

    const { masterUseCase, purchaseUseCase } = props;

    useEffect(() => {
        (async () => {
            try {
                dispatch(startFetching());
                await Promise.all([initiateOptions(), initiateDefaultData()]);
            } catch (error: any) {
                openErrorNoti();
            } finally {
                dispatch(completeFetching());
            }
        })();
    }, []);

    const initiateOptions = async () => {
        const options = await masterUseCase.getMasterSubCategoryGroupList({
            categoryCodes: MASTER_GROUP_CODES,
        });
        const masterDataInit: any = {
            ...options,
        };
        masterDataInit[MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION] = options[
            MASTER_CATEGORY_LIST.ADDRESSEE_DIV_CLASSIFICATION
        ]?.map((item: MasterSubCategory) => {
            return {
                label: item.codeName,
                value: item.code,
            };
        });
        masterDataInit[MASTER_CATEGORY_LIST.HOME_BUILDING_TYPE_CLASSIFICATION] = options[
            MASTER_CATEGORY_LIST.HOME_BUILDING_TYPE_CLASSIFICATION
        ]
            ?.filter((item: MasterSubCategory) => item.code !== '99')
            .map((item: MasterSubCategory) => {
                return {
                    label: item.codeName,
                    value: item.code,
                };
            });
        masterDataInit[MASTER_CATEGORY_LIST.HOME_POSSESSION_TYPE_CLASSIFICATION] = masterDataInit[
            MASTER_CATEGORY_LIST.HOME_POSSESSION_TYPE_CLASSIFICATION
        ]
            ?.filter((item: MasterSubCategory) => item.code !== '99')
            .map((item: MasterSubCategory) => {
                return {
                    label: item.codeName,
                    value: item.code,
                };
            });
        masterDataInit[MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_CLASSIFICATION] = masterDataInit[
            MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_CLASSIFICATION
        ]?.map((item: MasterSubCategory) => {
            return {
                label: item.codeName,
                value: item.code,
            };
        });
        setMasterData(masterDataInit);
    };

    const subtract = (a?: any, b?: any) => {
        const aNumber = isNaN(Number(a)) ? 0 : Number(a);
        const bNumber = isNaN(Number(b)) ? 0 : Number(b);
        return aNumber - bNumber;
    };

    const initiateDefaultData = async () => {
        const response = await Promise.all([
            purchaseUseCase.getDefaultInfoStep2({
                offerId: purchaseOfferId ? Number(purchaseOfferId) : 0,
            }),
            purchaseUseCase.getDefaultInfoStep3({
                listed_property_id: listedPropertyId ? Number(listedPropertyId) : 0,
                purchasePrice: purchasePrice ? Number(purchasePrice) : 0,
                purchaseOfferId: purchaseOfferId ? Number(purchaseOfferId) : 0,
                procedureId: procedureId ? Number(procedureId) : 0,
                loanUseStatus: loanUseStatus ? Number(loanUseStatus) : 0,
            }),
            purchaseUseCase.getDefaultInfo({
                offerId: purchaseOfferId ? Number(purchaseOfferId) : 0,
            }),
        ]);

        const deposit = commonHelpers.toNumberOnly(response[1].result?.deposit) ?? 0;
        const finalPayment = subtract(response[1].result?.totalPrice, deposit);
        if (response) {
            const mappingData = {
                ...response[0].result,
                ...response[1].result,
                ...response[2].result,
                finalPayment: finalPayment,
                procedureId: procedureId,
                purchaseOfferId: purchaseOfferId,
                listedPropertyId: listedPropertyId,
                purchasePrice: purchasePrice,
                loanUseStatus: loanUseStatus,
                includeBrokerageFee: response[1]?.result?.includeBrokerageFee ? [1] : [0],
                simId: simId,
            };
            setData(mappingData);
            form.setFieldsValue({
                ...mappingData,
                confirm_cic: '10',
                accept_term: '10',
            });
        }
    };

    const back = () => {
        const param = {
            listedPropertyId: listedPropertyId,
            procedureId: procedureId,
            purchaseOfferId: purchaseOfferId,
            purchasePrice: purchasePrice,
            loanUseStatus: loanUseStatus,
            step: 3,
        };
        navigate(
            `${PRIVATE_ROUTES.PATH}/${
                PRIVATE_ROUTES.SUB.CU_015_01
            }?${commonHelpers.generateQueryString(param)}`,
        );
    };

    const submit = async () => {
        try {
            dispatch(startFetching());
            const payload = {
                procedureId: data?.procedureId ? Number(data?.procedureId) : 0,
                purchaseOfferId: data?.purchaseOfferId ? Number(data?.purchaseOfferId) : 0,
            };
            const response = await props.purchaseUseCase.registerMemberIndiv(payload);
            if (response) {
                const alertProps: AlertModalProps = {
                    title: '購入申込を受け付けました',
                    message: '運営からの連絡をお待ちください。',
                    okText: 'ダッシュボードに戻る',
                    onOk: () => {
                        backToDashboard();
                    },
                };
                dispatch(showAlert(alertProps));
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const backToDashboard = () => {
        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.DASHBOARD}`);
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="CU-015-02-container"
        >
            <PageTitle title="購入申込みフォーム" />
            <section
                style={{
                    padding: '10px 0px',
                    height: '100%',
                    width: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'hidden',
                }}
            >
                <Form id="top-form" form={form} className="purchase-form-container w-full md:w-1/2">
                    <div className="section-wrapper">
                        <StepViewOne data={data} masterData={masterData} />
                    </div>
                    <div className="section-wrapper">
                        <StepViewTwo data={data} masterData={masterData} />
                    </div>
                    <div className="section-wrapper">
                        <StepViewThree data={data} masterData={masterData} />
                    </div>
                    <div className="section-wrapper">
                        <ButtonList next={submit} prev={back} />
                    </div>
                </Form>
            </section>
        </div>
    );
};

export default CU01502Page;
